import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PageHeader } from '../components/layout/AppShell';
import { SkeletonGrid } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { ListingCard } from '../components/ListingCard';
import { IconStore, IconCheck } from '../components/ui/icons';
import { API_BASE, ApiError, creators } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { buildStorefrontMeta, buildStorefrontJsonLd } from '../lib/og';
import type { CreatorStorefront as CreatorStorefrontData } from '../lib/types';

/**
 * Public creator storefront — Wingspan cut 9 (FINAL_STATE §7 catalog surface /
 * §2 room 8 Curator read-only half). The SEO "front door": a creator's profile
 * + published packs, shareable without an account. Crawler-facing OG/JSON-LD is
 * server-rendered by `functions/u/[handle].ts`; this screen adds the in-app
 * head via useDocumentMeta and renders the browsable grid.
 */
export function CreatorStorefront() {
  const { handle = '' } = useParams();
  const { t } = useI18n();
  const [data, setData] = useState<CreatorStorefrontData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    setError(null);
    creators
      .storefront(handle)
      .then((res) => {
        if (active) setData(res);
      })
      .catch((err) => {
        if (!active) return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 400)) setNotFound(true);
        else setError(apiErrorMessage(err, t));
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [handle, t]);

  const meta = useMemo(() => {
    if (!data) return null;
    const storefront = {
      creator: {
        id: data.creator.id,
        handle: data.creator.handle,
        display_name: data.creator.display_name,
        has_avatar: data.creator.avatar_url != null,
      },
      items: data.items.map((l) => ({
        id: l.id,
        title: l.title,
        price_cents: l.price_cents,
        price_currency: l.price_currency,
      })),
    };
    return {
      title: `${data.creator.display_name} — DJMEXXICO`,
      tags: buildStorefrontMeta(storefront),
      jsonLd: buildStorefrontJsonLd(storefront),
    };
  }, [data]);
  useDocumentMeta(meta);

  if (notFound) {
    return (
      <div>
        <EmptyState icon={<IconStore size={48} />} title={t.creator.notFoundTitle} body={t.creator.notFoundBody} />
        <div className="mt-6 text-center">
          <Link to="/market" className="type-label text-secondary hover:text-primary focus-ring inline-flex rounded-sm">
            ← {t.market.title}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <Link to="/market" className="type-label text-secondary hover:text-primary focus-ring inline-flex rounded-sm">
          ← {t.market.title}
        </Link>
      </div>

      {loading ? (
        <SkeletonGrid count={8} />
      ) : data ? (
        <>
          <div className="mb-6 flex items-center gap-4">
            {data.creator.avatar_url ? (
              <img
                src={`${API_BASE}${data.creator.avatar_url}`}
                alt=""
                className="h-16 w-16 border border-border-strong object-cover"
                loading="lazy"
              />
            ) : (
              <span className="text-muted flex h-16 w-16 items-center justify-center border border-border-strong bg-surface-2">
                <IconStore size={28} />
              </span>
            )}
            <div className="min-w-0">
              <PageHeader title={data.creator.display_name} subtitle={t.creator.subtitle} />
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="type-data text-muted tnum">{t.creator.packCount(data.count)}</span>
                {/* F-055: earned standing, shown as the co-signs it was earned from.
                    Only rendered once a producer has real standing (settled sales +
                    a co-sign) — never a hollow "0 co-signs". */}
                {data.creator.standing > 0 && data.creator.cosigns > 0 ? (
                  <span className="type-label text-amethyst-lite inline-flex items-center gap-1 tnum">
                    <IconCheck size={14} aria-hidden />
                    {t.creator.cosigns(data.creator.cosigns)}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {error ? (
            <p className="type-body-sm text-danger mb-4" role="alert">
              {error}
            </p>
          ) : null}

          {data.items.length === 0 ? (
            <EmptyState icon={<IconStore size={48} />} title={t.creator.emptyTitle} body={t.creator.emptyBody} />
          ) : (
            <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]" aria-live="polite">
              {data.items.map((l) => (
                <ListingCard key={l.id} listing={l} />
              ))}
            </div>
          )}
        </>
      ) : error ? (
        <p className="type-body-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
